"use client";

import { useEffect } from "react";

const HIDE_THRESHOLD = 80;
const SCROLL_TOLERANCE = 4;

/**
 * Hide-on-scroll-down, show-on-scroll-up for the sticky page header.
 *
 * Toggles `data-hidden="true"` on the nav root element (`[data-nav-root]`)
 * which Tailwind translates via `data-[hidden=true]:-translate-y-full`.
 *
 * Disabled while the body is scroll-locked (any modal/drawer open) so the
 * absolute-positioned search panel inside the header doesn't disappear with
 * the header on scroll.
 */
export const HeaderScrollBehavior = () => {
	useEffect(() => {
		const root = document.querySelector<HTMLElement>("[data-nav-root]");
		if (!root) return;

		let lastY = window.scrollY;
		let frame = 0;

		const update = () => {
			if (document.body.classList.contains("scroll-locked")) {
				root.removeAttribute("data-hidden");
				lastY = window.scrollY;
				return;
			}

			const y = window.scrollY;
			const dy = y - lastY;
			if (Math.abs(dy) < SCROLL_TOLERANCE) return;

			if (y > HIDE_THRESHOLD && dy > 0) {
				root.setAttribute("data-hidden", "true");
			} else if (dy < 0 || y <= HIDE_THRESHOLD) {
				root.removeAttribute("data-hidden");
			}

			lastY = y;
		};

		const onScroll = () => {
			if (frame) return;
			frame = requestAnimationFrame(() => {
				frame = 0;
				update();
			});
		};

		window.addEventListener("scroll", onScroll, { passive: true });
		return () => {
			if (frame) cancelAnimationFrame(frame);
			window.removeEventListener("scroll", onScroll);
		};
	}, []);

	return null;
};
